import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { CheckCircle, Package, Calendar, MapPin, BookOpen, User, Copy, Shield } from 'lucide-react';
import { toast } from 'sonner';

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'var(--background)'}}>
        <div className="text-xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'var(--background)'}}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Order not found</h2>
          <Button onClick={() => navigate('/')}>Go to Home</Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const isPendingLenderApproval =
    order.status === 'paid_pending_lender_approval' || order.lender_approval_status === 'pending';
  const isRejected = order.status === 'rejected_by_lender' || order.lender_approval_status === 'rejected';

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Header */}
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center mb-8">
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
            isRejected ? 'bg-red-500' : isPendingLenderApproval ? 'bg-amber-500' : ''
          }`} style={!isRejected && !isPendingLenderApproval ? {backgroundColor: '#10b981'} : undefined}>
            <CheckCircle className="h-12 w-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-2" style={{color: 'var(--primary)'}}>
            {isRejected ? 'Rental Declined' : isPendingLenderApproval ? 'Payment Received' : 'Order Placed Successfully!'}
          </h1>
          <p className="text-gray-600 text-lg">
            {isRejected
              ? 'The lender declined this rental. Our team will process your refund.'
              : isPendingLenderApproval
              ? 'Your payment was successful. The book lender will approve your request shortly. You will get WhatsApp/email when they approve — your pickup code appears only after approval. Track status under My Rentals.'
              : 'Thank you for your order. Your rental has been confirmed.'}
          </p>
          <p className="text-sm text-gray-500 mt-2">Order ID: <span className="font-mono font-semibold">{order.id}</span></p>
        </div>

        {/* Pickup Code Section - Only for pickup orders after lender approval */}
        {order.delivery_method === 'pickup' && order.pickup_code && !isPendingLenderApproval && !isRejected && (
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl shadow-md p-6 mb-6 border-2 border-amber-400">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold flex items-center text-amber-800">
                <Shield className="h-6 w-6 mr-2" />
                Your Pickup Code
              </h2>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                order.pickup_status === 'collected' ? 'bg-green-200 text-green-800' :
                order.pickup_status === 'ready' ? 'bg-blue-200 text-blue-800' :
                'bg-yellow-200 text-yellow-800'
              }`}>
                {order.pickup_status === 'collected' ? '✓ Collected' : 
                 order.pickup_status === 'ready' ? '📦 Ready for Pickup' : 
                 '⏳ Preparing'}
              </span>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center mb-4">
              <p className="text-sm text-gray-600 mb-2">Show this code to the book owner to collect your book</p>
              <div className="flex items-center justify-center space-x-4">
                <span className="text-4xl font-mono font-bold tracking-wider" style={{color: 'var(--primary)'}} data-testid="pickup-code">
                  {order.pickup_code}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(order.pickup_code);
                    toast.success('Pickup code copied!');
                  }}
                  data-testid="copy-pickup-code"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="bg-amber-50 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center">
                <User className="h-4 w-4 mr-2" />
                How to Pickup
              </h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Wait for notification that your book is ready</li>
                <li>• Visit the book owner&apos;s location</li>
                <li>• Share this code with the owner or security</li>
                <li>• Collect your book after verification</li>
              </ul>
            </div>
          </div>
        )}

        {/* Order Details */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <Package className="h-6 w-6 mr-2" style={{color: 'var(--primary)'}} />
            Order Details
          </h2>
          
          <div className="space-y-4">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
                <div className="flex items-center space-x-4">
                  <BookOpen className="h-8 w-8" style={{color: 'var(--primary)'}} />
                  <div>
                    <p className="font-semibold">{item.book_title}</p>
                    <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                  </div>
                </div>
                <p className="font-semibold">₹{Math.round(item.amount)}</p>
              </div>
            ))}
          </div>

          {(order.assigned_pincode || order.borrower_pincode) && (
            <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              <p className="font-medium flex items-center gap-1">
                <MapPin className="h-4 w-4" /> Nearest copy assigned
              </p>
              <p className="mt-1">
                Your order was matched to the book owner at pincode{' '}
                <strong>{order.assigned_pincode || '—'}</strong>
                {order.borrower_pincode ? ` (your delivery pincode: ${order.borrower_pincode}).` : '.'}
                {' '}When several copies exist, the system picks the nearest available owner automatically.
              </p>
            </div>
          )}

          <div className="border-t mt-6 pt-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span>₹{Math.round(order.total_amount)}</span>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>-₹{Math.round(order.discount)}</span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between font-bold text-lg">
              <span>Total Paid</span>
              <span style={{color: 'var(--accent)'}}>₹{Math.round(order.final_amount)}</span>
            </div>
          </div>
        </div>

        {/* Rental Period */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <Calendar className="h-6 w-6 mr-2" style={{color: 'var(--primary)'}} />
            Rental Period
          </h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
              <p className="text-sm text-gray-600 mb-1">Start Date</p>
              <p className="font-semibold">{formatDate(order.rental_start_date)}</p>
            </div>
            <div className="p-4 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
              <p className="text-sm text-gray-600 mb-1">Return Date</p>
              <p className="font-semibold">{formatDate(order.rental_end_date)}</p>
            </div>
          </div>
          
          <p className="text-sm text-gray-600 mt-4">
            <strong>Duration:</strong> {order.rental_duration_weeks} week{order.rental_duration_weeks > 1 ? 's' : ''}
          </p>
        </div>

        {/* Delivery Address */}
        {order.delivery_address && (
          <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <MapPin className="h-6 w-6 mr-2" style={{color: 'var(--primary)'}} />
              Delivery Address
            </h2>
            
            <div className="p-4 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
              <p className="font-semibold mb-1">{user?.name}</p>
              <p>{order.delivery_address.house_flat_no}, {order.delivery_address.street}</p>
              <p>{order.delivery_address.city}, {order.delivery_address.state} - {order.delivery_address.pincode}</p>
              <p className="mt-2">Phone: {order.delivery_address.phone}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/customer-dashboard')}
            className="flex-1"
            style={{backgroundColor: 'var(--primary)', color: 'white'}}
          >
            View My Orders
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="flex-1"
          >
            Continue Shopping
          </Button>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 rounded-2xl p-6 mt-6">
          <h3 className="font-bold mb-2">What&apos;s Next?</h3>
          {isPendingLenderApproval ? (
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✓ Payment received — thank you!</li>
              <li>⏳ The book lender has been notified to approve your request</li>
              <li>✓ You will receive your pickup code once the lender approves</li>
              <li>✓ Track status in &quot;My Rentals&quot; on your dashboard</li>
            </ul>
          ) : order.delivery_method === 'pickup' ? (
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✓ You will receive a confirmation email with your pickup code</li>
              <li>✓ The book owner will notify you when the book is ready for pickup</li>
              <li>✓ Visit the pickup location and share your code: <strong className="font-mono">{order.pickup_code}</strong></li>
              <li>✓ Please return the book by {formatDate(order.rental_end_date)} to avoid late fees</li>
              <li>✓ Track your pickup status in &quot;My Dashboard&quot;</li>
            </ul>
          ) : (
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✓ You will receive a confirmation email shortly</li>
              <li>✓ Your books will be delivered within 2-3 business days</li>
              <li>✓ Please return the books by {formatDate(order.rental_end_date)} to avoid late fees</li>
              <li>✓ Track your order status in &quot;My Dashboard&quot;</li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
